import store_laboratoire from '@/store/mods/analyses/laboratoire'
import RelectureAnalyse from './relecture-analyse'
import RelectureAnalyseCard from './RelectureAnalyse.vue'
import CompteRenduLaboratoire from '@/components/laboratoire/compte-rendu'

// LAB-006 : validation biologique — relecture du résultat saisi, interprétation, « Valider et diffuser » (une seule action)
// ou retour motivé à la saisie ; compte rendu du bon pour les lignes validées.
const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

// Les tests unitaires montent parfois cette vue sans le mixin global. Le repli
// conserve strictement la même présentation M/F sans jamais modifier la donnée.
const sexeCourt = (view, value) => {
    if (typeof view.formatSexeCourt === 'function') return view.formatSexeCourt(value)
    const source = value && typeof value === 'object' ? (value.specificatif || value.description || value.libelle || '') : value
    const raw = source == null ? '' : String(source).trim()
    const normalise = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalise)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalise)) return 'F'
    return raw || '—'
}

export default {
    name: 'validation-biologique',
    components: { CompteRenduLaboratoire, RelectureAnalyseCard },
    mixins: [RelectureAnalyse],
    props: [],
    data() {
        return {
            onglet: 'attente',
            vue: 'patients',
            onglets: [{ id: 'attente', nom: 'À valider' }, { id: 'valides', nom: 'Validés et diffusés' }],
            recherche: '', page: 1, pages: 1, total: 0,
            lignes: [], compteurs: null, is_loading: false, erreur: '', info: '', sequence: 0, detruit: false, search_timer: null,
            reserve: false,   // LAB-001-B : rôle actif non autorisé à valider (refus 403 de la Clinique)
            compte_rendu_bulletin_id: 0, compte_rendu_toggler: 0,
            relectures: [], modal_relectures: false, relectures_busy: {}, relectures_terminees: 0,
            confirmer_fermeture: false, fermeture_autorisee: false, destination_apres_fermeture: null,
            retirer_garde_navigation: null
        }
    },

    computed: {
        groupe_busy() { return Object.values(this.relectures_busy).some(Boolean) },
        rangees() {
            if (this.vue === 'analyses') return this.lignes.map(ligne => ({ key: ligne.demande_id, ligne, lignes: [ligne] }))
            const groupes = new Map()
            this.lignes.forEach(ligne => {
                const key = ligne.bulletin_id || ligne.reference || ligne.demande_id
                if (!groupes.has(key)) groupes.set(key, { key, ligne, lignes: [] })
                groupes.get(key).lignes.push(ligne)
            })
            return Array.from(groupes.values())
        }
    },

    mounted() {
        this.charger()
        window.addEventListener('beforeunload', this.avant_quitter)
        if (this.$router) this.retirer_garde_navigation = this.$router.beforeEach((to, from, next) => {
            if (!this.modal_relectures) return next()
            if (this.relecture_enregistrement()) return next(false)
            if (!this.relectures_modifiees()) return next()
            this.arreter_dictees()
            this.destination_apres_fermeture = to.fullPath
            this.confirmer_fermeture = true
            next(false)
        })
    },

    beforeDestroy() {
        this.detruit = true
        this.sequence++
        clearTimeout(this.search_timer)
        if (typeof window !== 'undefined') window.removeEventListener('beforeunload', this.avant_quitter)
        if (this.retirer_garde_navigation) this.retirer_garde_navigation()
    },

    methods: {
        ouvrir_relectures(lignes) {
            if (this.is_saving || this.modal_relectures || !lignes.length) return
            this.relectures = lignes.slice()
            this.relectures_busy = {}
            this.relectures_terminees = 0
            this.fermeture_autorisee = false
            this.destination_apres_fermeture = null
            this.modal_relectures = true
        },
        cartes_relecture() { return this.$refs.relectures || [] },
        relecture_enregistrement() { return this.groupe_busy || this.cartes_relecture().some(carte => carte.is_saving) },
        relectures_modifiees() { return this.cartes_relecture().some(carte => carte.hasDraft()) },
        relecture_enregistree(info) { this.relectures_terminees++; this.info = info },
        arreter_dictees() { this.cartes_relecture().forEach(carte => carte.arreter_dictee()) },
        avant_fermer_relectures(event) {
            this.arreter_dictees()
            if (this.relecture_enregistrement()) { event.preventDefault(); return }
            if (!this.fermeture_autorisee && this.relectures_modifiees()) {
                event.preventDefault()
                this.confirmer_fermeture = true
            }
        },
        continuer_relectures() {
            this.confirmer_fermeture = false
            this.destination_apres_fermeture = null
        },
        abandonner_relectures() {
            if (this.relecture_enregistrement()) return
            this.fermeture_autorisee = true
            this.confirmer_fermeture = false
            this.modal_relectures = false
        },
        relectures_fermees() {
            const destination = this.destination_apres_fermeture
            this.destination_apres_fermeture = null
            this.relectures = []
            this.fermeture_autorisee = false
            if (this.relectures_terminees) this.charger()
            if (destination && this.$router) this.$router.push(destination)
        },
        avant_quitter(event) {
            if (!this.modal_relectures || (!this.relecture_enregistrement() && !this.relectures_modifiees())) return
            event.preventDefault()
            event.returnValue = ''
        },
        charger() {
            if (this.detruit) return Promise.resolve()
            const sequence = ++this.sequence
            const data = { state: this.onglet, page: this.page, search_item: this.recherche.trim(), vue: this.vue }
            this.is_loading = true
            this.erreur = ''
            return store_laboratoire.getValidationListe(data).then((result) => {
                if (this.detruit || sequence !== this.sequence) return
                if (!result || !Array.isArray(result.data) || !result.counts) throw new Error('Réponse invalide')
                this.lignes = result.data
                this.compteurs = result.counts
                this.total = result.total
                this.pages = Math.max(1, result.last_page)
                this.is_loading = false
            }).catch((error) => {
                if (this.detruit || sequence !== this.sequence) return
                this.lignes = []
                this.compteurs = null
                this.total = 0
                this.is_loading = false
                if (error && error.response && error.response.status === 403) {
                    this.reserve = true
                    return
                }
                this.erreur = message(error, 'Impossible de charger la validation. Réessayez avec Actualiser.')
            })
        },

        choisir_onglet(id) {
            if (this.onglet === id) return
            this.onglet = id
            this.page = 1
            this.charger()
        },

        choisir_vue(vue) {
            if (this.vue === vue || !['analyses', 'patients'].includes(vue)) return
            this.vue = vue
            this.page = 1
            this.charger()
        },

        chercher() {
            clearTimeout(this.search_timer)
            this.search_timer = setTimeout(() => { this.page = 1; this.charger() }, 300)
        },

        aller_page(page) {
            if (page >= 1 && page <= this.pages) { this.page = page; this.charger() }
        },

        sexe_age(ligne) {
            const sexe = sexeCourt(this, ligne && ligne.sexe)
            return ligne && ligne.age ? sexe + ' / ' + ligne.age : sexe
        },

        ouvrir_compte_rendu(ligne) {
            this.compte_rendu_bulletin_id = ligne.bulletin_id
            this.compte_rendu_toggler = (this.compte_rendu_toggler === 0) ? 1 : 0
        }
    }
}
